import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedList, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { BrandS, LabelS } from '@sorare/core/src/atoms/typography';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import type { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import {
  anyPositionShortNames,
  positionNames,
} from '@sorare/core/src/lib/players';
import { ClickMarketHomeSection_Target } from '@sorare/core/src/protos/events/so5/shared/events';

import { AnyPlayerStatsIcon } from '#src/components/player/AnyPlayerStatsIcon/index.tsx';

import type { Performer_anyPlayer } from './__generated__/index.graphql.ts';

const Root = styled(Horizontal).attrs({ as: LinkBox })`
  background-color: var(--c-nd-50);
  border-radius: var(--unit);
  padding: 0 var(--intermediate-unit);
  align-items: stretch;
  min-width: 320px;
  min-height: 66px;
  height: 100%;
`;

const Metas = styled(Vertical).attrs({ gap: 0.5 })`
  flex-grow: 1;
  flex-shrink: 1;
  padding: var(--intermediate-unit) 0;
  align-self: center;
`;

const ImgWrapper = styled.div`
  position: relative;
  isolation: isolate;
  width: 60px;
  flex-shrink: 0;
  flex-grow: 0;
  overflow: hidden;
`;
const StyledResponsiveImg = styled(ResponsiveImg)`
  position: absolute;
  left: 0;
  top: var(--unit);
  width: 60px;
`;

const Scores = styled(Horizontal)`
  flex-shrink: 0;
  flex-grow: 0;
`;

const StyledBrandS = styled(BrandS)`
  white-space: nowrap;
`;

const POSITION_NAMES = {
  [Sport.FOOTBALL]: positionNames,
  [Sport.BASEBALL]: anyPositionShortNames,
  [Sport.NBA]: positionNames,
};

const useModes = (): Record<Sport, TotalAveragePlayerScore> => ({
  [Sport.FOOTBALL]: AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE,
  [Sport.BASEBALL]: AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
  [Sport.NBA]: AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
});

type Props = {
  anyPlayer: Performer_anyPlayer;
  onStatsClick: (slug: string) => void;
  onMarketClick: (slug: string) => void;
  track?: (p: {
    target: ClickMarketHomeSection_Target;
    playerSlug?: string;
  }) => void;
};

export const Performer = ({
  anyPlayer,
  onStatsClick,
  onMarketClick,
  track,
}: Props) => {
  const modes = useModes();
  const positionMessages = POSITION_NAMES[anyPlayer.sport];
  const mode = modes[anyPlayer.sport];
  return (
    <Root gap={2}>
      <ImgWrapper>
        <StyledResponsiveImg
          src={anyPlayer.fullPictureUrl}
          cropWidth={80}
          alt=""
        />
      </ImgWrapper>
      <Metas>
        <StyledBrandS>
          <LinkOverlay
            as="button"
            onClick={() => {
              onMarketClick(anyPlayer.slug);
              track?.({
                target: ClickMarketHomeSection_Target.PLAYER_MARKET_DRAWER,
                playerSlug: anyPlayer.slug,
              });
            }}
          >
            {anyPlayer.displayName}
          </LinkOverlay>
        </StyledBrandS>
        <LabelS color="var(--c-nd-600)">
          <FormattedList
            type="conjunction"
            value={anyPlayer.anyPositions.map(position => (
              <FormattedMessage
                key={position}
                {...positionMessages[position]}
              />
            ))}
          />
        </LabelS>
      </Metas>
      <Scores>
        <PlayerScore player={anyPlayer} mode={mode} animated={false} small />
        <LinkOther as="div">
          <AnyPlayerStatsIcon
            player={anyPlayer}
            onClick={() => {
              onStatsClick(anyPlayer.slug);
              track?.({
                target: ClickMarketHomeSection_Target.PLAYER_DETAILS,
                playerSlug: anyPlayer.slug,
              });
            }}
          />
        </LinkOther>
      </Scores>
    </Root>
  );
};

Performer.fragments = {
  anyPlayer: gql`
    fragment Performer_anyPlayer on AnyPlayerInterface {
      slug
      sport
      displayName
      fullPictureUrl
      anyPositions
      ...AnyPlayerStatsIcon_anyPlayer
      ...PlayerScore_anyPlayer
    }
    ${AnyPlayerStatsIcon.fragments.anyPlayer}
    ${PlayerScore.fragments.anyPlayer}
  ` as TypedDocumentNode<Performer_anyPlayer>,
};
